import React, { useEffect, useState } from "react";
import { Table, Tag, Button, message, Select, Space, Typography, Input, Row, Col } from "antd";
import axios from "axios";
import SummaryApi from "../../../common/index";
import { RedoOutlined, FilePdfOutlined } from "@ant-design/icons";

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

function OrdersPage({ token }) {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const statusColors = {
    pending: "orange",
    completed: "green",
    cancelled: "red",
    returned: "purple",
    paid: "green",
    failed: "red",
    ready_for_pickup: "blue",
    pay_at_store: "gold",
  };

  // ✅ Fetch Orders
  const fetchOrders = async (status = "all") => {
    setLoading(true);
    try {
      let url =
        status === "all"
          ? SummaryApi.getSellerOrders.url
          : SummaryApi.getSellerOrdersByStatus.url.replace(":status", status);

      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("🔹 Fetching orders with status:", status);
      console.log("🔹 Final URL:", url);

      if (data.success && Array.isArray(data.orders)) {
        setOrders(data.orders);
        setFilteredOrders(data.orders);
      } else {
        setOrders([]);
        setFilteredOrders([]);
        message.warning("No orders found");
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      message.error("Failed to fetch orders ❌");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // ✅ Search Filter Logic
  useEffect(() => {
    const filtered = orders.filter((order) => {
      const customerName = order.user_name?.toLowerCase() || "";
      const productNames = order.products
        ?.map((p) => p.product_name.toLowerCase())
        .join(", ") || "";
      return (
        customerName.includes(searchTerm.toLowerCase()) ||
        productNames.includes(searchTerm.toLowerCase())
      );
    });
    setFilteredOrders(filtered);
  }, [searchTerm, orders]);

  // ✅ Update Order Status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const { data } = await axios({
        url: SummaryApi.updateOrders(orderId).url,
        method: SummaryApi.updateOrders(orderId).method,
        headers: { Authorization: `Bearer ${token}` },
        data: { status: newStatus },
      });

      if (data.success || data.message === "Order status updated successfully") {
        message.success(`Order marked as ${newStatus.replaceAll("_", " ")}`);
        fetchOrders(filterStatus);
      } else {
        message.error("Failed to update order status ❌");
      }
    } catch (err) {
      console.error("Error updating order:", err);
      message.error("Server error while updating order ❌");
    }
  };

  // ✅ Generate Invoice PDF
  const handleGenerateInvoice = async (orderId) => {
    try {
      const response = await axios({
        url: SummaryApi.generateSellerInvoice(orderId).url,
        method: SummaryApi.generateSellerInvoice(orderId).method,
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const fileURL = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `invoice_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      message.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error generating invoice:", error);
      message.error("Failed to generate invoice ❌");
    }
  };

  // ✅ Table Columns
  const columns = [
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
      render: (order_number) => order_number || "-",
    },
    {
      title: "Customer Name",
      dataIndex: "user_name",
      key: "user_name",
      render: (name) => name || "-",
    },
    {
      title: "Products",
      key: "products",
      render: (_, record) => (
        <span>{record.products.map((p) => p.product_name).join(", ")}</span>
      ),
    },
    {
      title: "Quantity",
      key: "quantity",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {record.products.map((p) => (
            <div key={p.product_id}>{p.quantity}</div>
          ))}
        </div>
      ),
    },
    {
      title: "Total Price",
      dataIndex: "total_price",
      key: "total_price",
      align: "center",
      render: (total) => `₹${total}`,
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      align: "center",
      render: (method) => (
        <Tag color={method === "pay_at_store" ? "gold" : "green"}>
          {method?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => {
        const formattedStatus = status
          ?.toLowerCase()
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase());
        return (
          <Tag color={statusColors[status.toLowerCase()] || "blue"}>
            {formattedStatus}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => {
        const { status, order_number } = record;

        return (
          <Space>
            {status === "pending" && (
              <Button
                danger
                onClick={() => updateOrderStatus(record.order_id, "cancelled")}
              >
                Cancel
              </Button>
            )}

            {(status === "pay_at_store" || status === "paid") && (
              <>
                <Button
                  style={{background:"#1129ce",color:"white"}}
                  onClick={() =>
                    updateOrderStatus(record.order_id, "ready_for_pickup")
                  }
                >
                  Ready for Pickup
                </Button>
                <Button
                  style={{background:"#1129ce",color:"white"}}
                  onClick={() => updateOrderStatus(record.order_id, "completed")}
                >
                  Completed
                </Button>
              </>
            )}

            {status === "ready_for_pickup" && (
              <>
                <Button
                    style={{background:"#1129ce",color:"white"}}
                  onClick={() =>
                    updateOrderStatus(record.order_id, "completed")
                  }
                >
                  Completed
                </Button>
                <Button
                  danger
                  onClick={() => updateOrderStatus(record.order_id, "cancelled")}
                >
                  Cancel
                </Button>
              </>
            )}

            {status === "completed" && (
              <>
                <Button
                    style={{background:"#1129ce",color:"white"}}
                  icon={<FilePdfOutlined />}
                  onClick={() => handleGenerateInvoice(record.order_id)}
                >
                  Generate Invoice
                </Button>
                <Button
                  icon={<RedoOutlined />}
                  onClick={() => updateOrderStatus(record.order_id, "returned")}
                >
                  Return
                </Button>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Title level={3} style={{ marginBottom: "20px" }}>
        Seller Orders Management
      </Title>

      <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            defaultValue="all"
            style={{ width: "100%" }}
            onChange={(value) => {
              const status = value.toLowerCase().trim();
              setFilterStatus(status);
              fetchOrders(status);
            }}
          >
            <Option value="all">All Orders</Option>
            <Option value="pending">Pending</Option>
            <Option value="paid">Paid</Option>
            <Option value="pay_at_store">Pay at Store</Option>
            <Option value="ready_for_pickup">Ready for Pickup</Option>
            <Option value="completed">Completed</Option>
            <Option value="cancelled">Cancelled</Option>
            <Option value="returned">Returned</Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <Search
            placeholder="Search by product or customer name"
            allowClear
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      <Table
        loading={loading}
        dataSource={filteredOrders}
        columns={columns}
        rowKey="order_id"
        bordered
        pagination={{ pageSize: 8 }}
      />
    </div>
  );
}

export default OrdersPage;
